import React from 'react';
import { Card } from '../components/ui/Card';
import { formatCurrency } from '../utils/calculations';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
export function NetProfit() {
  const profitData = {
    revenue: [{
      name: 'Service Revenue',
      amount: 285000
    }, {
      name: 'Product Sales',
      amount: 125000
    }, {
      name: 'Consulting Fees',
      amount: 45000
    }, {
      name: 'Other Income',
      amount: 8000
    }],
    costOfGoodsSold: [{
      name: 'Materials',
      amount: 95000
    }, {
      name: 'Direct Labor',
      amount: 120000
    }, {
      name: 'Subcontractors',
      amount: 35000
    }],
    operatingExpenses: [{
      name: 'Salaries & Wages',
      amount: 85000
    }, {
      name: 'Rent',
      amount: 24000
    }, {
      name: 'Utilities',
      amount: 6000
    }, {
      name: 'Insurance',
      amount: 12000
    }, {
      name: 'Marketing & Advertising',
      amount: 15000
    }, {
      name: 'Office Supplies',
      amount: 4000
    }, {
      name: 'Professional Services',
      amount: 8000
    }, {
      name: 'Depreciation',
      amount: 10000
    }, {
      name: 'Other Expenses',
      amount: 7000
    }],
    otherIncomeExpenses: [{
      name: 'Interest Income',
      amount: 2000
    }, {
      name: 'Interest Expense',
      amount: -5000
    }]
  };
  const totalRevenue = profitData.revenue.reduce((sum, item) => sum + item.amount, 0);
  const totalCOGS = profitData.costOfGoodsSold.reduce((sum, item) => sum + item.amount, 0);
  const grossProfit = totalRevenue - totalCOGS;
  const grossMargin = grossProfit / totalRevenue * 100;
  const totalOperatingExpenses = profitData.operatingExpenses.reduce((sum, item) => sum + item.amount, 0);
  const operatingIncome = grossProfit - totalOperatingExpenses;
  const operatingMargin = operatingIncome / totalRevenue * 100;
  const totalOtherIncomeExpenses = profitData.otherIncomeExpenses.reduce((sum, item) => sum + item.amount, 0);
  const netIncome = operatingIncome + totalOtherIncomeExpenses;
  const netMargin = netIncome / totalRevenue * 100;
  return <div className="space-y-6" data-id="element-1867">
      <div data-id="element-1868">
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1869">Net Profit</h1>
        <p className="text-sm text-gray-600" data-id="element-1870">
          Profit & Loss Statement for{' '}
          {new Date().toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric'
        })}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1871">
        <Card data-id="element-1872">
          <div className="flex items-center justify-between" data-id="element-1873">
            <div data-id="element-1874">
              <p className="text-sm text-gray-600 mb-1" data-id="element-1875">Gross Profit</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1876">
                {formatCurrency(grossProfit)}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-1877">
                {grossMargin.toFixed(1)}% margin
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-1878">
              <TrendingUpIcon className="w-6 h-6 text-blue-600" data-id="element-1879" />
            </div>
          </div>
        </Card>

        <Card data-id="element-1880">
          <div className="flex items-center justify-between" data-id="element-1881">
            <div data-id="element-1882">
              <p className="text-sm text-gray-600 mb-1" data-id="element-1883">Operating Income</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-1884">
                {formatCurrency(operatingIncome)}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-1885">
                {operatingMargin.toFixed(1)}% margin
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center" data-id="element-1886">
              <TrendingUpIcon className="w-6 h-6 text-purple-600" data-id="element-1887" />
            </div>
          </div>
        </Card>

        <Card data-id="element-1888">
          <div className="flex items-center justify-between" data-id="element-1889">
            <div data-id="element-1890">
              <p className="text-sm text-gray-600 mb-1" data-id="element-1891">Net Income</p>
              <p className="text-2xl font-bold text-green-600" data-id="element-1892">
                {formatCurrency(netIncome)}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-1893">
                {netMargin.toFixed(1)}% margin
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center" data-id="element-1894">
              <TrendingUpIcon className="w-6 h-6 text-green-600" data-id="element-1895" />
            </div>
          </div>
        </Card>
      </div>

      {/* Revenue */}
      <Card data-id="element-1896">
        <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1897">Revenue</h2>
        <div className="space-y-2" data-id="element-1898">
          {profitData.revenue.map((item, index) => <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100" data-id="element-1899">
              <span className="text-gray-700" data-id="element-1900">{item.name}</span>
              <span className="font-medium text-gray-900" data-id="element-1901">
                {formatCurrency(item.amount)}
              </span>
            </div>)}
          <div className="flex justify-between items-center py-3 font-bold bg-blue-50 px-3 rounded" data-id="element-1902">
            <span className="text-gray-900" data-id="element-1903">Total Revenue</span>
            <span className="text-blue-600" data-id="element-1904">
              {formatCurrency(totalRevenue)}
            </span>
          </div>
        </div>
      </Card>

      {/* Cost of Goods Sold */}
      <Card data-id="element-1905">
        <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1906">
          Cost of Goods Sold
        </h2>
        <div className="space-y-2" data-id="element-1907">
          {profitData.costOfGoodsSold.map((item, index) => <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100" data-id="element-1908">
              <span className="text-gray-700" data-id="element-1909">{item.name}</span>
              <span className="font-medium text-gray-900" data-id="element-1910">
                {formatCurrency(item.amount)}
              </span>
            </div>)}
          <div className="flex justify-between items-center py-3 font-bold bg-red-50 px-3 rounded" data-id="element-1911">
            <span className="text-gray-900" data-id="element-1912">Total COGS</span>
            <span className="text-red-600" data-id="element-1913">{formatCurrency(totalCOGS)}</span>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t-2 border-gray-300" data-id="element-1914">
          <div className="flex justify-between items-center" data-id="element-1915">
            <span className="text-lg font-bold text-gray-900" data-id="element-1916">
              Gross Profit
            </span>
            <span className="text-lg font-bold text-blue-600" data-id="element-1917">
              {formatCurrency(grossProfit)}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1 text-right" data-id="element-1918">
            {grossMargin.toFixed(1)}% margin
          </p>
        </div>
      </Card>

      {/* Operating Expenses */}
      <Card data-id="element-1919">
        <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1920">
          Operating Expenses
        </h2>
        <div className="space-y-2" data-id="element-1921">
          {profitData.operatingExpenses.map((item, index) => <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100" data-id="element-1922">
              <span className="text-gray-700" data-id="element-1923">{item.name}</span>
              <span className="font-medium text-gray-900" data-id="element-1924">
                {formatCurrency(item.amount)}
              </span>
            </div>)}
          <div className="flex justify-between items-center py-3 font-bold bg-orange-50 px-3 rounded" data-id="element-1925">
            <span className="text-gray-900" data-id="element-1926">Total Operating Expenses</span>
            <span className="text-orange-600" data-id="element-1927">
              {formatCurrency(totalOperatingExpenses)}
            </span>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t-2 border-gray-300" data-id="element-1928">
          <div className="flex justify-between items-center" data-id="element-1929">
            <span className="text-lg font-bold text-gray-900" data-id="element-1930">
              Operating Income
            </span>
            <span className="text-lg font-bold text-purple-600" data-id="element-1931">
              {formatCurrency(operatingIncome)}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1 text-right" data-id="element-1932">
            {operatingMargin.toFixed(1)}% margin
          </p>
        </div>
      </Card>

      {/* Other Income/Expenses */}
      <Card data-id="element-1933">
        <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1934">
          Other Income & Expenses
        </h2>
        <div className="space-y-2" data-id="element-1935">
          {profitData.otherIncomeExpenses.map((item, index) => <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100" data-id="element-1936">
              <span className="text-gray-700" data-id="element-1937">{item.name}</span>
              <span className={`font-medium ${item.amount >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-1938">
                {formatCurrency(item.amount)}
              </span>
            </div>)}
          <div className="flex justify-between items-center py-3 font-bold bg-gray-50 px-3 rounded" data-id="element-1939">
            <span className="text-gray-900" data-id="element-1940">Total Other Income/Expenses</span>
            <span className={totalOtherIncomeExpenses >= 0 ? 'text-green-600' : 'text-red-600'} data-id="element-1941">
              {formatCurrency(totalOtherIncomeExpenses)}
            </span>
          </div>
        </div>
      </Card>

      {/* Net Income */}
      <Card className="bg-green-50" data-id="element-1942">
        <div className="flex justify-between items-center mb-2" data-id="element-1943">
          <span className="text-2xl font-bold text-gray-900" data-id="element-1944">Net Income</span>
          <span className="text-2xl font-bold text-green-600" data-id="element-1945">
            {formatCurrency(netIncome)}
          </span>
        </div>
        <div className="flex justify-between items-center text-sm" data-id="element-1946">
          <span className="text-gray-600" data-id="element-1947">Net Profit Margin</span>
          <span className="font-semibold text-green-600" data-id="element-1948">
            {netMargin.toFixed(1)}%
          </span>
        </div>
      </Card>
    </div>;
}